// Install runtime polyfills (e.g. ES2025 Set methods) before any app code runs.
import './polyfills'
// TODO we probably don't need these React imports here
import 'react-dates/initialize'
// @ts-expect-error TS2882 — strictNullChecks. TODO: fix strict null check
import 'react-dates/lib/css/_datepicker.css'
import ReactModal from 'react-modal'
import { createRoot } from 'react-dom/client'
import { lazy } from 'react'
import {
  SHARED_COMPONENTS_ES_ES,
  SHARED_COMPONENTS_FR_FR,
} from '@retailer-platform/shared-components/src/exports/shared-components-core'
import {
  SHARED_ENTERPRISE_TOOLS_ES_ES,
  SHARED_ENTERPRISE_TOOLS_FR_FR,
} from '@retailer-platform/domain-shared-enterprise-tools/shared-enterprise-tools-core'
// eslint-disable-next-line @retailer-platform/no-restricted-imports
import { instacart } from '@retailer-platform/dashboard/legacy-core'
import { initSentry } from '@retailer-platform/dashboard/errors'
import { initAnalytics, initDataDogRum } from '@retailer-platform/dashboard/analytics'
import { markBootStart, trackStaticAssetLoadTime } from '@retailer-platform/dashboard/performance'
import { RPPCore } from '@retailer-platform/dashboard/app'
import { intlDevFailLoud } from './nav-labels/intlDevFailLoud'
import { DOMAIN_ROUTES } from './domainRoutes'
import domainCatalogDeps from './nav-labels/__generated__/domainCatalogDeps.json'
import { clientEnv } from '@retailer-platform/dashboard/env'
import navLabelsEnUs from './nav-labels/__generated__/en-US.json'
import { EAGER_EN_US_CATALOG } from './eagerEnUsCatalog'

/**
 * Initialize analytics services
 *
 * Strategy:
 * - Sentry: Always initialize (error tracking is essential)
 * - Analytics & Datadog: Handled by PrivacyComplianceProvider based on user region
 *   - If OneTrust configured: PrivacyComplianceProvider detects region via IP
 *     - EU users: Load OneTrust and wait for consent
 *     - Non-EU users: Auto-enable analytics (no OneTrust needed)
 *   - If OneTrust NOT configured: Initialize immediately (backward compatible)
 */
initSentry() // Always initialize error tracking

// Start timing for framework boot measurement
markBootStart()
trackStaticAssetLoadTime()

// Check if OneTrust is configured (GDPR consent management)
const hasOneTrustConfigured = !!clientEnv.PUBLIC_CLIENT_ONETRUST_DOMAIN_UUID

if (hasOneTrustConfigured) {
  // OneTrust configured: PrivacyComplianceProvider will handle region detection and analytics initialization
  console.log(
    '[Analytics Init] OneTrust configured - PrivacyComplianceProvider will handle region-based initialization'
  )

  // Note: initAnalytics() and initDataDogRum() will be called by:
  // 1. PrivacyComplianceProvider.tsx - detects user region via IP
  //    - EU users: Loads OneTrust and waits for consent
  //    - Non-EU users: Auto-enables analytics (implied consent)
  // 2. PublicLogIn.tsx (for public/login pages) - listens for OneTrust consent changes
} else {
  // No OneTrust: Initialize analytics immediately (backward compatible)
  console.log('[Analytics Init] No OneTrust configured - initializing analytics immediately')
  void initAnalytics()
  initDataDogRum()
}

/* Vendor Setup */
ReactModal.setAppElement('#root')

instacart.init({ mode: (process.env.NODE_ENV || 'development') as NodeEnv })
const NativePartnerOnboardingGate = lazy(() =>
  import('@retailer-platform/domain-onboarding/pending-partner-onboarding').then(module => ({
    default: module.PartnerOnboardingGate,
  }))
)
/**
 * We create an instance of the application,
 * this will allow us to register domains
 */

// The thunk defers the native route chunk (and its @tanstack bytes) behind a dynamic
// import; the flag-on shell is the only caller, so the flag-off boot never loads it.
// The same chunk also registers the visited domain's definition on demand — that
// registration is driven from `domainRegistration`, which needs the `app` instance.
const app = new RPPCore({
  loadNativeRoutes: () => import('./nativeRoutes').then(module => module.nativeRoutes),
  renderNativePartnerContent: children => (
    <NativePartnerOnboardingGate>{children}</NativePartnerOnboardingGate>
  ),
})

// Dev-only: fail loud when a rendered message id's catalog is not loaded (the
// raw-id incident class under route-scoped domain catalogs). Undefined in
// prod, so the IntlProvider behavior there is unchanged.
app.setDevIntlOnError(intlDevFailLoud)

// Generated domain → catalog-requirement manifest. RPPCore widens route
// catalog loading to owner ∪ catalogDeps, making route-scoped en-US safe for
// cross-domain component composition (the CXPPA-26 incident class).
app.setCatalogDepsManifest(
  domainCatalogDeps as unknown as Parameters<RPPCore['setCatalogDepsManifest']>[0]
)

app.addTranslations({
  'en-US': EAGER_EN_US_CATALOG,
  'es-ES': {
    ...SHARED_COMPONENTS_ES_ES,
    ...SHARED_ENTERPRISE_TOOLS_ES_ES,
  },
  'fr-FR': {
    ...SHARED_COMPONENTS_FR_FR,
    ...SHARED_ENTERPRISE_TOOLS_FR_FR,
  },
})

// Eager nav-label base. The global sidebar renders every domain's nav labels,
// but domain en-US catalogs are route-scoped (lazy) — so an off-route domain's
// label would flash its raw message id until visited. This build-generated
// manifest supplies just those labels eagerly; the full lazy catalog overrides
// them on route entry. Regenerate: pnpm --filter @retailer-platform/app-ipp nav-labels:update
app.addTranslations({ 'en-US': navLabelsEnUs.labels })

/**
 * Actually start the application
 *
 * Uses lazy domain loading to run domain import in parallel with account fetch.
 * The domain registry is split into 4 chunks that download and parse in parallel,
 * reducing the JS parse bottleneck from a single large chunk.
 */
const startApp = () => {
  app.setRouteCatalog(DOMAIN_ROUTES)
  const domainPromise = import('./domainRegistration').then(m => m.bootstrap(app))

  // Start rendering immediately - account fetch will begin in AppProviderPreloader
  // while domain import continues in the background
  app.start(domainPromise, tree => {
    const container = document.getElementById('root')
    // @ts-expect-error TS2345 — strictNullChecks. TODO: fix strict null check
    const root = createRoot(container)
    root.render(tree)
  })
}

startApp()
